/**
 * RowItem — read-only label/value row used inside profile/account/quick-action
 * cards. Stacks label (caption) over value (body2) with a thin divider between
 * rows when used inside a list.
 *
 * Intentionally simple so the parent card can compose any number of these as
 * `<RowItem label=… value=… />` without buying into a bigger primitive.
 */

import { Box, GlassChip, type GlassChipProps, Typography } from '@ops-matrix/design-system-ui';
import type { ReactElement, ReactNode } from 'react';

export interface RowItemProps {
  label: ReactNode;
  /** Primary value (string or any node — e.g. a chip + text combo). */
  value: ReactNode;
  /** Optional small chip to the right of the value (status, count). */
  chipLabel?: string;
  chipColor?: GlassChipProps['color'];
  /** Optional trailing action node (button / link). */
  action?: ReactNode;
  /** Hide the bottom border (for the last row in a list). */
  isLast?: boolean;
}

export function RowItem({
  label,
  value,
  chipLabel,
  chipColor = 'default',
  action,
  isLast,
}: RowItemProps): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        minHeight: 44,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.25 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {value}
          </Typography>
          {chipLabel && <GlassChip label={chipLabel} size="small" color={chipColor} />}
        </Box>
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
}

export default RowItem;
